import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Button,
  Tabs,
  Tab,
  Form,
  InputGroup,
  ListGroup,
  Badge,
  Spinner
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faListAlt,
  faSearch,
  faTimes,
  faPlus,
  faBuilding,
  faHandshake
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import api from '@services/api';

const WishesModal = ({
  show,
  onHide,
  participant,
  salonId,
  salon,
  onWishesUpdate
}) => {
  const [loading, setLoading] = useState(false);
  const [wishes, setWishes] = useState([]);
  const [availableEntities, setAvailableEntities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('current');

  const isPartner = participant?.type === 'partner';

  useEffect(() => {
    if (show && participant) {
      // Réinitialiser les vœux avec ceux du participant à chaque ouverture
      setWishes(participant.wishes || []);
      prepareData();
    }
  }, [show, participant, salon]);

  const prepareData = () => {
    if (!participant || !salon) return;

    // Récupérer les vœux du participant actuel
    const currentWishes = participant.wishes || [];
    setWishes(currentWishes);

    // Récupérer les entités disponibles (entreprises pour un participant partenaire, partenaires pour un participant entreprise)
    if (isPartner) {
      // Récupérer toutes les entreprises du salon
      setAvailableEntities(salon.salon_companies || []);
    } else {
      // Récupérer tous les partenaires du salon
      setAvailableEntities(salon.salon_partners || []);
    }

    setLoading(false);
  };

  const handleToggleWish = async (entityId, isWished) => {
    if (!participant || !salonId) return;

    setLoading(true);
    try {
      // CORRECTION DE L'URL
      const endpoint = `/admin/partner-salons/${salonId}/wishes`;

      const payload = isPartner
        ? {
            partner_participant_id: participant.id,
            company_id: entityId,
            is_wished: !isWished
          }
        : {
            company_participant_id: participant.id,
            partner_id: entityId,
            is_wished: !isWished
          };

      const response = await api.post(endpoint, payload);

      if (response.data.success) {
        let updatedWishes = [];
        if (!isWished) {
          // Ajouter le nouveau vœu
          const newWish = response.data.data;
          updatedWishes = [...wishes, newWish];
          setWishes(updatedWishes);
        } else {
          // Supprimer le vœu existant
          updatedWishes = wishes.filter(wish =>
            isPartner
              ? wish.company_id !== entityId
              : wish.partner_id !== entityId
          );
          setWishes(updatedWishes);
        }

        // Appeler le callback avec les vœux mis à jour
        if (onWishesUpdate) {
          onWishesUpdate({
            participantId: participant.id,
            type: participant.type,
            wishesCount: updatedWishes.length,
            updatedWishes: updatedWishes
          });
        }

        toast(isWished ? 'Vœu supprimé avec succès' : 'Vœu ajouté avec succès');
      } else {
        toast('Erreur lors de la mise à jour du vœu');
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du vœu:', error);
      toast('Une erreur est survenue lors de la mise à jour du vœu');
    } finally {
      setLoading(false);
    }
  };

  const isEntityWished = entityId => {
    if (isPartner) {
      return wishes.some(wish => wish.company_id === entityId);
    } else {
      return wishes.some(wish => wish.partner_id === entityId);
    }
  };

  const getFilteredEntities = () => {
    return availableEntities.filter(entity =>
      entity.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getWishedEntities = () => {
    if (!wishes || !availableEntities) return [];

    return wishes.map(wish => {
      // Récupérer l'entité associée au vœu
      let entityName;

      if (isPartner && wish.company) {
        entityName = wish.company.name;
      } else if (!isPartner && wish.partner) {
        entityName = wish.partner.name;
      } else {
        // Si nous n'avons pas les données relation chargées, chercher dans les entités disponibles
        const entityField = isPartner ? 'company_id' : 'partner_id';
        const entity = availableEntities.find(e => e.id === wish[entityField]);
        entityName = entity ? entity.name : `ID: ${wish[entityField]}`;
      }

      return {
        ...wish,
        entityName
      };
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faListAlt} className="me-2" />
          Vœux de {participant?.firstname} {participant?.lastname}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center p-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Chargement des vœux...</p>
          </div>
        ) : (
          <>
            <InputGroup className="mb-3">
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <Form.Control
                placeholder={`Rechercher ${
                  isPartner ? 'une entreprise' : 'un partenaire'
                }...`}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setSearchTerm('')}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              )}
            </InputGroup>

            <Tabs
              activeKey={activeTab}
              onSelect={k => setActiveTab(k)}
              className="mb-3"
            >
              <Tab
                eventKey="current"
                title={
                  <span>
                    <FontAwesomeIcon icon={faListAlt} className="me-2" />
                    Vœux actuels ({getWishedEntities().length})
                  </span>
                }
              >
                {getWishedEntities().length === 0 ? (
                  <div className="alert alert-info">
                    Aucun vœu n'a été enregistré pour ce participant.
                  </div>
                ) : (
                  <ListGroup>
                    {getWishedEntities().map(wish => (
                      <ListGroup.Item
                        key={wish.id}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <FontAwesomeIcon
                            icon={isPartner ? faBuilding : faHandshake}
                            className="me-2"
                          />
                          {wish.entityName}
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() =>
                            handleToggleWish(
                              isPartner ? wish.company_id : wish.partner_id,
                              true
                            )
                          }
                          disabled={loading}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Tab>
              <Tab
                eventKey="available"
                title={
                  <span>
                    <FontAwesomeIcon
                      icon={isPartner ? faBuilding : faHandshake}
                      className="me-2"
                    />
                    Tous les {isPartner ? 'entreprises' : 'partenaires'}
                  </span>
                }
              >
                {getFilteredEntities().length === 0 ? (
                  <div className="alert alert-info">
                    Aucun résultat trouvé pour "{searchTerm}".
                  </div>
                ) : (
                  <ListGroup>
                    {getFilteredEntities().map(entity => {
                      const isWished = isEntityWished(entity.id);
                      return (
                        <ListGroup.Item
                          key={entity.id}
                          className={`d-flex justify-content-between align-items-center ${
                            isWished ? 'list-group-item-success' : ''
                          }`}
                        >
                          <div>
                            <FontAwesomeIcon
                              icon={isPartner ? faBuilding : faHandshake}
                              className="me-2"
                            />
                            {entity.name}
                          </div>
                          <Button
                            variant={
                              isWished ? 'outline-danger' : 'outline-success'
                            }
                            size="sm"
                            onClick={() =>
                              handleToggleWish(entity.id, isWished)
                            }
                            disabled={loading}
                          >
                            <FontAwesomeIcon
                              icon={isWished ? faTimes : faPlus}
                            />
                          </Button>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                )}
              </Tab>
            </Tabs>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Badge bg="primary" className="me-auto">
          {wishes.length} vœux au total
        </Badge>
        <Button variant="secondary" onClick={onHide}>
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

WishesModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  participant: PropTypes.object,
  salonId: PropTypes.string.isRequired,
  salon: PropTypes.object,
  onWishesUpdate: PropTypes.func
};

WishesModal.defaultProps = {
  participant: null,
  salon: null
};

export default WishesModal;
